import { gql, QueryResult } from '@apollo/client'
import * as React from 'react'
import styled from 'styled-components'
import { clock } from '../../../../common/src/clock'
import Button from '../../../../common/src/components/Button'
import { IntroText } from '../../../../common/src/components/core'
import { handleError } from '../../../../common/src/data/errors'
import { FetchJobsQuery, FetchJobsQueryVariables } from '../../../../common/src/types.gen'
import JobsTable from './JobsTable'
import { PollingQuery } from './PollingQuery'

export const fetchJobsQuery = gql`
  query FetchJobs($jobKey: String!) {
    jobs(jobKey: $jobKey) {
      id
      jobName
      jobKey
      jobStatus
      timeCreated
      timeFinished
    }
  }
`

interface JobsHistorySectionProps {
  jobKey: string
  buttons?: IJobButton[]
  compact?: boolean
}

interface IJobButton {
  label: string
  handler: () => Promise<any>
}

class JobsHistorySectionImpl extends React.PureComponent<
  JobsHistorySectionProps & QueryResult<FetchJobsQuery, FetchJobsQueryVariables>,
  { timeNow: string; runningJobs: Set<string> }
> {
  state = { timeNow: clock.now(), runningJobs: new Set<string>() }
  private runHandlers: any = {}

  componentDidMount() {
    clock.addListener(this.onClock)
  }

  componentWillUnmount() {
    clock.removeListener(this.onClock)
  }

  private onClock = (timeNow: string) => this.setState({ timeNow })

  render() {
    const { jobKey, data, buttons, compact } = this.props
    if (data == null || data.jobs == null) {
      return null
    }

    const { jobs } = data

    const buttonElems = (buttons || []).map(button => (
      <JobButton
        key={button.label}
        disabled={this.isRunning(jobKey, button.label)}
        onClick={this.getRunHandler(jobKey, button.label, button.handler)}
        color="white"
        style={{ verticalAlign: 'top' }}
        size="small"
      >
        {button.label}
      </JobButton>
    ))

    return (
      <div>
        {buttonElems.length > 0 && <JobButtonsWrap>{buttonElems}</JobButtonsWrap>}
        <JobsTable jobs={jobs} compact={compact} />
      </div>
    )
  }

  private isRunning(jobKey: string, label: string) {
    return this.state.runningJobs.has([jobKey, label].join(' '))
  }

  private getRunHandler(jobKey: string, label: string, handler: () => Promise<void>) {
    const key = [jobKey, label].join(' ')
    this.runHandlers[key] = this.runHandlers[key] || (() => this.run(key, handler))
    return this.runHandlers[key]
  }

  private run(key: string, handler: () => Promise<void>) {
    const { runningJobs } = this.state
    if (!runningJobs.add(key)) {
      return
    }
    this.setState({ runningJobs })
    handler()
      .then(() => {
        runningJobs.delete(key)
        return this.setState({ runningJobs })
      })
      .then(() => this.props.refetch())
      .catch(handleError)
  }
}

const JobButtonsWrap = styled(IntroText)`
  line-height: 30px;
`

const JobButton = styled(Button)`
  margin-right: 16px;
`

export default function JobsHistorySection(props: JobsHistorySectionProps) {
  return (
    <PollingQuery query={fetchJobsQuery} variables={{ jobKey: props.jobKey }}>
      {(res: QueryResult<FetchJobsQuery, FetchJobsQueryVariables>) => <JobsHistorySectionImpl {...props} {...res} />}
    </PollingQuery>
  )
}
